import mapboxgl, { LayerSpecification, type ExpressionSpecification } from 'mapbox-gl'
import { format } from 'date-fns'
import circle from '@turf/circle'
import distance from '@turf/distance'
import center from '@turf/center'
import bearing from '@turf/bearing'
import { bearingToAzimuth, multiPoint } from '@turf/helpers'
import type { FeatureCollection, Feature, LineString, Position } from 'geojson'
import { locationPinStyle } from './styles/location-pin'
import { resourcePinStyle } from './styles/resource-pin'

export const GeoJsonGeometryType = {
  Point: 'Point',
  LineString: 'LineString',
  MultiLineString: 'MultiLineString',
} as const

export type GeoJsonGeometryType = (typeof GeoJsonGeometryType)[keyof typeof GeoJsonGeometryType]

interface CreateJobTripLayersOptions {
  sourceName: string
  layerIdPrefix: string
  routeColor?: string
  routeBorderColor?: string
  breadcrumbColor?: string
  includeResources?: boolean
  includeLocations?: boolean
  locationTypes?: string[]
}

export interface MultiJobPalette {
  routeColor: string
  routeBorderColor: string
  breadcrumbColor: string
}

export const multiJobColorPalette: MultiJobPalette[] = [
  {
    routeColor: '#2563eb',
    routeBorderColor: '#1e3a8a',
    breadcrumbColor: '#3b82f6',
  },
  {
    routeColor: '#16a34a',
    routeBorderColor: '#14532d',
    breadcrumbColor: '#22c55e',
  },
  {
    routeColor: '#ea580c',
    routeBorderColor: '#7c2d12',
    breadcrumbColor: '#f97316',
  },
  {
    routeColor: '#b45309',
    routeBorderColor: '#78350f',
    breadcrumbColor: '#d97706',
  },
  {
    routeColor: '#0f766e',
    routeBorderColor: '#134e4a',
    breadcrumbColor: '#14b8a6',
  },
  {
    routeColor: '#ca8a04',
    routeBorderColor: '#713f12',
    breadcrumbColor: '#eab308',
  },
  {
    routeColor: '#dc2626',
    routeBorderColor: '#7f1d1d',
    breadcrumbColor: '#ef4444',
  },
  {
    routeColor: '#0284c7',
    routeBorderColor: '#0c4a6e',
    breadcrumbColor: '#38bdf8',
  },
  {
    routeColor: '#7c3aed',
    routeBorderColor: '#4c1d95',
    breadcrumbColor: '#8b5cf6',
  },
  {
    routeColor: '#db2777',
    routeBorderColor: '#831843',
    breadcrumbColor: '#ec4899',
  },
  {
    routeColor: '#65a30d',
    routeBorderColor: '#365314',
    breadcrumbColor: '#84cc16',
  },
  {
    routeColor: '#9333ea',
    routeBorderColor: '#581c87',
    breadcrumbColor: '#a855f7',
  },
  {
    routeColor: '#be123c',
    routeBorderColor: '#881337',
    breadcrumbColor: '#e11d48',
  },
  {
    routeColor: '#0891b2',
    routeBorderColor: '#164e63',
    breadcrumbColor: '#06b6d4',
  },
  {
    routeColor: '#4f46e5',
    routeBorderColor: '#312e81',
    breadcrumbColor: '#6366f1',
  },
]

export const getMultiJobPaletteByIndex = (index: number): MultiJobPalette => {
  const normalizedIndex =
    ((index % multiJobColorPalette.length) + multiJobColorPalette.length) %
    multiJobColorPalette.length

  return multiJobColorPalette[normalizedIndex]
}

export const buildMultiJobIndexLookup = (jobIds: string[]) => {
  return new Map(jobIds.map((jobId, index) => [jobId, index]))
}

const isBreadcrumb = (feature: Feature) => feature.properties?.Type === 'Breadcrumb'

const isRouteSegment = (feature: Feature): feature is Feature<LineString> =>
  feature.geometry.type === GeoJsonGeometryType.LineString &&
  (feature.properties?.SegmentType === 'Stop' || feature.properties?.SegmentType === 'Travel')

export const formatJobTripGeoJson = (data?: FeatureCollection | null): FeatureCollection | null => {
  if (!data) return null

  let features = data.features

  // Add new features derived from V2 routes (breadcrumbs and stop circles)
  if (data.features.some(isRouteSegment)) {
    const breadcrumbs = buildBreadcrumbsFromRoutes(data.features.filter(isRouteSegment))
    const stopCircles = buildStopCircles(data.features.filter(isRouteSegment))
    features = features.concat(breadcrumbs, stopCircles)
  }

  const [start, end] = findStartAndEnd(features.filter(isBreadcrumb))
  features = features.map((feature) => {
    if (feature === start) return markEndpoint(feature, 'StartPoint')
    if (feature === end) return markEndpoint(feature, 'EndPoint')
    return feature
  })

  return { ...data, features }
}

const buildBreadcrumbsFromRoutes = (routeFeatures: Feature<LineString>[]): Feature[] => {
  const breadcrumbs: Feature[] = []

  for (const route of routeFeatures) {
    if (route.geometry.coordinates.length < 2) continue

    route.geometry.coordinates.forEach((coord, index) => {
      const breadcrumb: Feature = {
        type: 'Feature',
        id: crypto.randomUUID(),
        properties: {
          Timestamp: new Date(
            Date.parse(route.properties?.StartTime) + route.properties?.StartTimeOffsets?.[index],
          ).toISOString(),
          Heading:
            index === route.geometry.coordinates.length - 1
              ? computeHeading(route.geometry.coordinates[index - 1], coord)
              : computeHeading(coord, route.geometry.coordinates[index + 1]),
          Type: 'Breadcrumb',
          UnitNumber: route.properties?.UnitNumber ?? '',
          UserName: route.properties?.UserName ?? '',
        },
        geometry: {
          coordinates: coord,
          type: 'Point',
        },
      }
      breadcrumbs.push(breadcrumb)
    })
  }

  return breadcrumbs
}

const computeHeading = (from: Position, to: Position): number => bearingToAzimuth(bearing(from, to))

// TODO: Collapse the functions to find and mark start and end into build breadcrumbs
// once all the trip maps go through that flow
const findStartAndEnd = (breadcrumbs: Feature[]): [Feature, Feature] | [] => {
  if (breadcrumbs.length < 2) return []

  const sorted = [...breadcrumbs].sort(
    (a, b) => Date.parse(a.properties?.Timestamp) - Date.parse(b.properties?.Timestamp),
  )
  return [sorted[0], sorted[sorted.length - 1]]
}

const markEndpoint = (feature: Feature, subType: 'StartPoint' | 'EndPoint'): Feature => {
  const timestamp = new Date(feature.properties?.Timestamp)

  return {
    ...feature,
    properties: {
      ...feature.properties,
      SubType: subType,
      Description: format(timestamp, 'MMM dd, HH:mm'),
    },
  }
}

// We add two features per stop. The stop area polygon is a fixed size and shows the exact
// bounds of the stop at a close zoom. The stop area marker has a variable size and shows
// the location of stops at further out zoom levels.
const buildStopCircles = (routes: Feature<LineString>[]): Feature[] => {
  return routes
    .filter((route) => route.properties?.SegmentType === 'Stop')
    .flatMap<Feature>((route) => {
      const coordinates = route.geometry.coordinates

      if (!coordinates.length || !route.properties) return []

      const properties = {
        UnitNumber: route.properties.UnitNumber,
        UserName: route.properties.UserName,
        StartTime: route.properties.StartTime,
        EndTime: route.properties.EndTime,
        DistanceTraveled: route.properties.DistanceTraveled,
      }

      const centerPoint = center(multiPoint(coordinates))
      const radius = Math.max(
        ...coordinates.map((coordinate) => distance(centerPoint, coordinate, { units: 'meters' })),
      )

      return [
        circle(centerPoint.geometry.coordinates, Math.max(radius * 1.01, 1), {
          units: 'meters',
          properties: {
            Type: 'StopArea',
            ...properties,
          },
        }),
        {
          type: 'Feature',
          geometry: { type: 'Point', coordinates: centerPoint.geometry.coordinates },
          properties: {
            Type: 'StopAreaMarker',
            ...properties,
          },
        },
      ]
    })
}

export const buildBoundsFromCollections = (
  collections: Array<FeatureCollection | null | undefined>,
): mapboxgl.LngLatBounds | null => {
  const bounds = new mapboxgl.LngLatBounds()
  let hasCoordinates = false

  collections.forEach((collection) => {
    collection?.features.forEach((feature) => {
      if (feature.geometry.type === GeoJsonGeometryType.Point) {
        bounds.extend(feature.geometry.coordinates as [number, number])
        hasCoordinates = true
      } else if (feature.geometry.type === GeoJsonGeometryType.LineString) {
        for (const coord of feature.geometry.coordinates) {
          bounds.extend(coord as [number, number])
          hasCoordinates = true
        }
      } else if (feature.geometry.type === GeoJsonGeometryType.MultiLineString) {
        for (const group of feature.geometry.coordinates) {
          for (const coord of group) {
            bounds.extend(coord as [number, number])
            hasCoordinates = true
          }
        }
      }
    })
  })

  return hasCoordinates ? bounds : null
}

export const fitMapToBounds = (
  map: mapboxgl.Map | undefined,
  mini: boolean | undefined,
  bounds: mapboxgl.LngLatBounds,
): boolean => {
  if (!map) return false

  const mapFitOptions = mini
    ? { padding: 60 }
    : {
        padding: {
          top: 150,
          bottom: 150,
          left: 150,
          right: 150 + 500,
        },
      }

  map.fitBounds(bounds, {
    ...mapFitOptions,
    maxZoom: 16,
  })

  return true
}

export const fitMapToFeatureCollections = (
  map: mapboxgl.Map | undefined,
  mini: boolean | undefined,
  collections: Array<FeatureCollection | null | undefined>,
) => {
  const bounds = buildBoundsFromCollections(collections)
  if (!bounds) return false
  return fitMapToBounds(map, mini, bounds)
}

export const createJobTripLayers = ({
  sourceName,
  layerIdPrefix,
  routeColor = 'CornflowerBlue',
  routeBorderColor = 'DarkBlue',
  breadcrumbColor = 'blue',
  includeResources = true,
  includeLocations = false,
  locationTypes = [],
}: CreateJobTripLayersOptions): LayerSpecification[] => {
  const highlightRouteSegment: ExpressionSpecification = [
    'all',
    ['boolean', ['feature-state', 'hover'], false],
    ['in', ['get', 'SegmentType'], ['literal', ['Travel', 'Stop']]],
  ]

  const layers: LayerSpecification[] = [
    {
      id: `${layerIdPrefix}-stop-area-markers`,
      type: 'circle',
      source: sourceName,
      maxzoom: 16,
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 26, 14, 20, 16, 0],
        'circle-color': [
          'case',
          ['boolean', ['feature-state', 'hover'], false],
          '#FFC35C',
          routeColor,
        ],
        'circle-opacity': 0.5,
        'circle-stroke-color': [
          'case',
          ['boolean', ['feature-state', 'hover'], false],
          '#FFC35C',
          routeColor,
        ],
        'circle-stroke-width': 2,
        'circle-stroke-opacity': 0.7,
      },
      filter: ['==', ['get', 'Type'], 'StopAreaMarker'],
    },
    {
      id: `${layerIdPrefix}-stop-areas-fill`,
      type: 'fill',
      source: sourceName,
      paint: {
        'fill-color': [
          'case',
          ['boolean', ['feature-state', 'hover'], false],
          '#FFC35C',
          routeColor,
        ],
        'fill-opacity': 0.5,
      },
      filter: ['==', ['get', 'Type'], 'StopArea'],
    },
    {
      id: `${layerIdPrefix}-stop-areas`,
      type: 'line',
      source: sourceName,
      paint: {
        'line-color': [
          'case',
          ['boolean', ['feature-state', 'hover'], false],
          '#FFC35C',
          routeColor,
        ],
        'line-width': 2,
        'line-opacity': 0.7,
      },
      filter: ['==', ['get', 'Type'], 'StopArea'],
    },
    {
      id: `${layerIdPrefix}-routes-border`,
      type: 'line',
      source: sourceName,
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': ['case', highlightRouteSegment, '#D18400', routeBorderColor],
        'line-width': ['case', highlightRouteSegment, 14, 10],
      },
      filter: ['==', '$type', 'LineString'],
    },
    {
      id: `${layerIdPrefix}-routes`,
      type: 'line',
      source: sourceName,
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': ['case', highlightRouteSegment, '#FFC35C', routeColor],
        'line-width': ['case', highlightRouteSegment, 10, 8],
      },
      filter: ['==', '$type', 'LineString'],
    },
    {
      id: `${layerIdPrefix}-breadcrumb-circle`,
      source: sourceName,
      type: 'circle',
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 13, 0, 14, 3, 22, 10],
        'circle-color': [
          'case',
          ['boolean', ['feature-state', 'hover'], false],
          '#FFC35C',
          breadcrumbColor,
        ],
        'circle-stroke-color': 'white',
        'circle-stroke-width': ['interpolate', ['linear'], ['zoom'], 13, 0, 22, 2],
      },
      minzoom: 13,
      filter: ['==', ['get', 'Type'], 'Breadcrumb'],
    },
    {
      id: `${layerIdPrefix}-breadcrumb-bearing`,
      source: sourceName,
      type: 'symbol',
      layout: {
        'text-field': '➔',
        'text-size': ['interpolate', ['linear'], ['zoom'], 14, 0, 16, 10, 22, 20],
        'text-rotate': ['-', ['get', 'Heading'], 90],
      },
      paint: {
        'text-color': 'white',
        'text-translate': [-0.1, 0],
      },
      filter: ['==', ['get', 'Type'], 'Breadcrumb'],
    },
    {
      id: `${layerIdPrefix}-start-finish`,
      source: sourceName,
      type: 'symbol',
      layout: {
        'icon-size': 1,
        'icon-rotate': ['get', 'Heading'],
        'icon-allow-overlap': true,
        'icon-rotation-alignment': 'map',
        'icon-image': 'arrow-standby',
        'text-field': ['get', 'Description'],
        'text-font': ['Montserrat Medium', 'Arial Unicode MS Regular'],
        'text-radial-offset': 1,
        'text-line-height': 1.1,
        'text-optional': true,
        'text-variable-anchor': ['right', 'left', 'top', 'bottom'],
        'text-size': ['interpolate', ['cubic-bezier', 0.5, 0, 1, 1], ['zoom'], 11, 11, 15, 16],
      },
      paint: {
        'text-halo-color': 'hsl(0, 5%, 0%)',
        'text-color': 'white',
        'text-halo-width': ['case', ['boolean', ['feature-state', 'hover'], false], 3, 2],
        'text-halo-blur': 0.5,
      },
      filter: [
        'any',
        ['==', ['get', 'SubType'], 'StartPoint'],
        ['==', ['get', 'SubType'], 'EndPoint'],
      ],
    },
  ]

  if (includeResources) {
    layers.push({
      id: `${layerIdPrefix}-resources`,
      type: 'symbol',
      source: sourceName,
      layout: {
        ...resourcePinStyle.layout,
        'symbol-sort-key': 0,
        'icon-size': 1.5,
        'icon-allow-overlap': false,
        'icon-optional': false,
        'text-radial-offset': 2,
        'text-allow-overlap': true,
        'text-optional': false,
        'text-size': {
          stops: [
            [15, 11],
            [15.5, 12],
            [17, 14],
          ],
        },
      },
      paint: {
        ...resourcePinStyle.paint,
        'text-opacity': 1,
      },
      filter: ['==', ['get', 'Type'], 'Resource'],
    })
  }

  if (includeLocations && locationTypes.length > 0) {
    layers.push({
      id: `${layerIdPrefix}-locations`,
      source: sourceName,
      type: 'symbol',
      metadata: { hover: false },
      layout: {
        ...locationPinStyle.layout,
        'symbol-sort-key': 0,
        'icon-allow-overlap': true,
        'icon-size': 1.5,
        'text-radial-offset': 2,
        'text-allow-overlap': true,
        'text-optional': false,
        'text-size': {
          stops: [
            [15, 11],
            [15.5, 12],
            [17, 14],
          ],
        },
      },
      paint: {
        ...locationPinStyle.paint,
        'text-opacity': 1,
      },
      filter:
        locationTypes.length === 1
          ? ['==', ['get', 'Type'], locationTypes[0]]
          : ['any', ...locationTypes.map((type) => ['==', ['get', 'Type'], type])],
    })
  }

  return layers
}
